import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import { PhotoLabel } from 'dashboard/components/ChecksTableRow/Components';
import Permissioned from 'dashboard/components/Permissioned';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { VerificationGovernmentIdNfc } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Sticker from 'design-system/components/Sticker';
import { COUNTRIES } from 'lib/constants/locales';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${Spacing[4]};
  padding-top: ${Spacing[2]};
`;

const InfoSection = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

interface IdNfcVerificationProps extends VerificationDetailProps {
  verification: VerificationGovernmentIdNfc;
}

const IdNfcVerification = ({ verification }: IdNfcVerificationProps) => {
  const {
    'name-first': nameFirst,
    'name-last': nameLast,
    birthdate,
    status,
    'redacted-at': redactedAt,
    'identification-number': identificationNumber,
    'issuing-country': issuingCountry,
    'expiration-date': expirationDate,
    'portrait-photo-url': portraitPhotoUrl,
  } = verification.attributes;

  const redacted = redactedAt != null;
  const showChecks = ![
    VerificationStatus.Initiated,
    VerificationStatus.Confirmed,
    VerificationStatus.Submitted,
    VerificationStatus.Canceled,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ].includes(status);

  const checks = verification.attributes.checks;
  const nameFull = [nameFirst, nameLast].filter(Boolean).join(' ');

  return (
    <InfoSection>
      <ImageGridContainer>
        <div>
          <AsyncImage
            imageUrl={portraitPhotoUrl}
            width='initial'
            height='250px'
            modalLabel='Portrait photo'
            allowModalExpand={true}
          />
          <PhotoLabel>Portrait photo</PhotoLabel>
        </div>
      </ImageGridContainer>
      <StyledAttributeGrid data-test='verification-detail__id-nfc-verification'>
        <Attribute
          label='ID number'
          value={
            <Permissioned
              permission={Permission.VerificationGovIdNumberView}
              fallback={
                <Sticker variant='basic' data-test='verification-detail__id-nfc-id__not-available'>
                  Not Available
                </Sticker>
              }
            >
              {identificationNumber}
            </Permissioned>
          }
          redacted={redacted}
        />
        <Attribute label='Full name' value={nameFull} redacted={redacted} />
        <Attribute label='Birthdate' value={formatDate(birthdate)} redacted={redacted} />
        <Attribute
          label='Issuing country'
          value={issuingCountry ? COUNTRIES[issuingCountry] : null}
          redacted={redacted}
        />
        <Attribute label='Expiration date' value={formatDate(expirationDate)} redacted={redacted} />
      </StyledAttributeGrid>
      {showChecks && checks && <ChecksTable checks={checks} />}
    </InfoSection>
  );
};

export default IdNfcVerification;
